// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { ProductStateResponse } from "@bitebalance/contracts"
import { useI18n, formatEurFromCents } from "@/i18n"
import { InfoButton } from "@/components/ui/InfoButton"
import { goalStatusWithDelta } from "@/lib/glossary"

/**
 * Today (Budget Model v2, ADR-052 §11.3): ONE compact status line against
 * the SOFT goal — "Forecast ≈ €X/week · Above goal · €Z above goal" — with
 * ⓘ. Rendered only when a goal is set AND the forecast is available;
 * otherwise nothing (no card, no placeholder). The exact signed delta is
 * always shown next to the status word (owner D-3, no tolerance band).
 */
export function BudgetTodayLine({
  budgetEconomics,
}: {
  budgetEconomics: ProductStateResponse["budgetEconomics"] | null
}) {
  const { t, locale } = useI18n()
  if (!budgetEconomics) return null
  const goal = budgetEconomics.current?.goal
  const forecast = budgetEconomics.forecastWeeklyAverage
  if (
    budgetEconomics.weeklyGoalCents === null ||
    !goal ||
    goal.status === "unavailable" ||
    !forecast.available ||
    forecast.cents === null
  ) {
    return null
  }
  return (
    <p
      role="status"
      data-testid="today-budget-line"
      className="flex items-center gap-1 text-sm text-ink-2"
    >
      <span>
        {t("views.budget.todayLine", {
          forecast: formatEurFromCents(forecast.cents, locale),
          status: goalStatusWithDelta(goal.status, goal.deltaCents, locale),
        })}
      </span>
      <InfoButton termId="forecast-weekly-average" />
    </p>
  )
}
